import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { LayoutGrid, TrendingUp, AlertTriangle, Download } from "lucide-react";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend,
} from "recharts";

const BRAND = ["#003DA5", "#4070BC", "#001F54", "#FFB547", "#2E8B57", "#D9534F", "#809ED2", "#BFCFE8"];

export default function CoverageDashboard() {
  const [payload, setPayload] = useState(null);
  const [categories, setCategories] = useState([]);
  const [models, setModels] = useState([]);
  const [agents, setAgents] = useState([]);
  const [f, setF] = useState({ category_id: "", state: "", agent_id: "" });

  useEffect(() => {
    api.get("/product-categories").then(r => setCategories(r.data));
    api.get("/products").then(r => setModels(r.data));
    api.get("/agents").then(r => setAgents(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const params = {};
    Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/product-coverage", { params }).then(r => setPayload(r.data));
  }, [f]);

  const doExport = async (fmt) => {
    try {
      const res = await api.get("/reports/product_coverage", { params: { fmt }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `MLILY-Coverage-Dashboard.${fmt}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  if (!payload) return <div className="text-slate-500">Loading coverage dashboard…</div>;
  const summary = payload.summary;
  const totalStores = summary[0]?.total || 0;
  const totalCovered = summary.reduce((a, s) => a + s.displayed, 0);
  const totalNotCovered = summary.reduce((a, s) => a + s.not_displayed, 0);
  const totalUnchecked = summary.reduce((a, s) => a + s.unchecked, 0);
  const avgCoverage = summary.length ? Math.round(summary.reduce((a, s) => a + s.coverage_pct, 0) / summary.length) : 0;

  // Aggregate by category & state & agent from rows
  const byCat = {}, byState = {}, byAgent = {}, byGroup = {};
  payload.rows.forEach(r => {
    if (r.status !== "displayed") return;
    byCat[r.category_name] = (byCat[r.category_name] || 0) + 1;
    byState[r.state || "?"] = (byState[r.state || "?"] || 0) + 1;
    byAgent[r.agent_id || "?"] = (byAgent[r.agent_id || "?"] || 0) + 1;
    byGroup[r.customer_group || "Other"] = (byGroup[r.customer_group || "Other"] || 0) + 1;
  });
  const agentName = (id) => agents.find(a => a.id === id)?.name || id;

  return (
    <div className="space-y-6" data-testid="coverage-dashboard-page">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight flex items-center gap-2"><LayoutGrid className="w-8 h-8 text-[#003DA5]" /> Product Coverage Dashboard</h1>
          <p className="text-slate-600 mt-1">Model-level coverage across all your stores — size filter never changes the model coverage %</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => doExport("xlsx")} className="h-11 px-4 border rounded-lg font-semibold text-sm flex items-center gap-2 hover:border-[#003DA5]" data-testid="export-cov-xlsx"><Download className="w-4 h-4" /> Excel</button>
          <button onClick={() => doExport("pdf")} className="h-11 px-4 border rounded-lg font-semibold text-sm flex items-center gap-2 hover:border-[#003DA5]" data-testid="export-cov-pdf"><Download className="w-4 h-4" /> PDF</button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap gap-2">
        <select value={f.category_id} onChange={e => setF({ ...f, category_id: e.target.value })} className="h-10 px-3 border rounded-lg text-sm" data-testid="filter-category">
          <option value="">All categories</option>{categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={f.state} onChange={e => setF({ ...f, state: e.target.value })} className="h-10 px-3 border rounded-lg text-sm" data-testid="filter-state">
          <option value="">All states</option>{["NSW","VIC","QLD","SA","WA","TAS","NT","ACT"].map(s => <option key={s}>{s}</option>)}
        </select>
        <select value={f.agent_id} onChange={e => setF({ ...f, agent_id: e.target.value })} className="h-10 px-3 border rounded-lg text-sm" data-testid="filter-agent">
          <option value="">All agents</option>{agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Eligible Stores" value={totalStores} testid="kpi-eligible" />
        <Kpi label="Covered (any model)" value={totalCovered} tone="green" testid="kpi-covered" />
        <Kpi label="Not Covered" value={totalNotCovered} tone="red" testid="kpi-not-covered" />
        <Kpi label="Not Yet Checked" value={totalUnchecked} tone="neutral" testid="kpi-unchecked" />
        <Kpi label="Avg. Coverage" value={`${avgCoverage}%`} tone="blue" testid="kpi-avg" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Coverage % by Model">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={summary} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
              <XAxis type="number" domain={[0, 100]} unit="%" tick={{ fontSize: 11 }} />
              <YAxis dataKey="model_name" type="category" tick={{ fontSize: 11 }} width={110} />
              <Tooltip formatter={v => `${v}%`} />
              <Bar dataKey="coverage_pct" radius={[0, 6, 6, 0]}>{summary.map((s, i) => <Cell key={i} fill={s.coverage_pct >= 70 ? "#2E8B57" : s.coverage_pct >= 40 ? "#003DA5" : "#D9534F"} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Displayed by Category">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={Object.entries(byCat).map(([k, v]) => ({ name: k, value: v }))} dataKey="value" nameKey="name" outerRadius={90} label>
                {Object.keys(byCat).map((_, i) => <Cell key={i} fill={BRAND[i % BRAND.length]} />)}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Displayed by State">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={Object.entries(byState).map(([k, v]) => ({ name: k, value: v }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="value" fill="#003DA5" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Displayed by Sales Agent">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={Object.entries(byAgent).map(([k, v]) => ({ name: agentName(k), value: v }))} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={110} />
              <Tooltip />
              <Bar dataKey="value" fill="#001F54" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <ChartCard title="Model Coverage Table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500 bg-[#F3F7FC]">
              <tr>
                <th className="text-left px-3 py-2">Category</th>
                <th className="text-left px-3 py-2">Model</th>
                <th className="text-left px-3 py-2">✓ Covered</th>
                <th className="text-left px-3 py-2">✕ Not</th>
                <th className="text-left px-3 py-2">— Unchecked</th>
                <th className="text-left px-3 py-2">Coverage %</th>
              </tr>
            </thead>
            <tbody>
              {summary.map(s => (
                <tr key={s.model_id} className="border-t" data-testid={`cov-model-${s.model_id}`}>
                  <td className="px-3 py-2 text-slate-600">{s.category_name}</td>
                  <td className="px-3 py-2 font-medium">{s.model_name}</td>
                  <td className="px-3 py-2 text-emerald-600 font-semibold">{s.displayed}</td>
                  <td className="px-3 py-2 text-red-600 font-semibold">{s.not_displayed}</td>
                  <td className="px-3 py-2 text-slate-500">{s.unchecked}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full ${s.coverage_pct >= 70 ? "bg-emerald-500" : s.coverage_pct >= 40 ? "bg-[#003DA5]" : "bg-red-500"}`} style={{ width: `${s.coverage_pct}%` }} />
                      </div>
                      <span className="text-xs font-semibold">{s.coverage_pct}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>
    </div>
  );
}

const Kpi = ({ label, value, tone, testid }) => {
  const c = tone === "green" ? "text-emerald-600" : tone === "red" ? "text-red-600" : tone === "blue" ? "text-[#003DA5]" : tone === "neutral" ? "text-slate-500" : "text-[#001F54]";
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{label}</div>
      <div className={`text-2xl font-heading font-bold mt-1 ${c}`}>{value}</div>
    </div>
  );
};
const ChartCard = ({ title, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5">
    <h3 className="text-lg font-heading font-semibold mb-3">{title}</h3>
    {children}
  </div>
);
